"use client"

import type React from "react"
import { useState } from "react"
import { Edit3, Trash2, Star, X } from "lucide-react"
import type { ILeadership } from "../../../../shared/models/three-sixty-feedback-models/Leadership"
import type { IValue } from "../../../../shared/models/three-sixty-feedback-models/Values"
import EditLeadershipModal from "../modals/EditLeadershipModal"
import Modal from "../../../../shared/components/Modal"
import MODAL_NAMES from "../../../dialogs/ModalName"
import showModalFromId from "../../../../shared/functions/ModalShow"
import { useAppContext } from "../../../../shared/functions/Context"
import EditValueModal from "../modals/EditValueModal"

interface IProps {
  value: (IValue | ILeadership)[]
  onRate?: (rating: number) => void
  inputFeaturesActive?: boolean
}

export const CriteriaBox: React.FC<IProps> = ({ value }) => {
  const { api, store } = useAppContext()
  const [selectedItem, setSelectedItem] = useState<IValue | ILeadership | null>(null)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<IValue | ILeadership | null>(null)
  const [values, setValues] = useState<(IValue | ILeadership)[]>(value)
  const [selectedRatings, setSelectedRatings] = useState<{ [key: string]: number }>({})

  const toggleEdit = (item: IValue | ILeadership) => {
    setSelectedItem(item)
    if (isValue(item)) {
      const value = store.value.getItemById(item.id)?.asJson
      if (value) {
        store.value.select(value)
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_VALUE_MODAL)
      }
    } else {
      const leadership = store.leadership.getItemById(item.id)?.asJson
      if (leadership) {
        store.leadership.select(leadership)
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_LEADERSHIP_MODAL)
      }
    }
  }

  const toggleDelete = (item: IValue | ILeadership) => {
    setItemToDelete(item)
    setDeleteConfirmationOpen(true)
  }

  const onDeleteValue = async (valueToDelete: IValue) => {
    try {
      await api.value.delete(valueToDelete)
      setDeleteConfirmationOpen(false)
      const updatedValueList = values.filter((item) => item.id !== valueToDelete.id)
      setValues(updatedValueList)
    } catch (error) {
      console.error("Error deleting value:", error)
    }
  }

  const onDeleteLeadership = async (leadershipToDelete: ILeadership) => {
    try {
      await api.leadership.delete(leadershipToDelete)
      setDeleteConfirmationOpen(false)
      const updatedLeadershipList = values.filter((item) => item.id !== leadershipToDelete.id)
      setValues(updatedLeadershipList)
    } catch (error) {
      console.error("Error deleting leadership:", error)
    }
  }

  const handleRating = (itemId: string, statementIndex: number, rating: number) => {
    const key = `${itemId}-${statementIndex}`
    setSelectedRatings((prev) => ({ ...prev, [key]: rating }))
  }

  function isValue(item: IValue | ILeadership): item is IValue {
    return "valueName" in item
  }

  return (
    <div className="space-y-6">
      {values.map((item, index) => (
        <div
          key={index}
          className="group relative bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300"
        >
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-indigo-600/90"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{isValue(item) ? item.valueName : item.criteriaName}</h3>
                  <p className="text-blue-100 text-sm">{isValue(item) ? "Company Value" : "Leadership Criteria"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleEdit(item)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors duration-200 backdrop-blur-sm group"
                  title="Edit"
                >
                  <Edit3 className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
                </button>
                <button
                  onClick={() => toggleDelete(item)}
                  className="p-2 bg-white/20 hover:bg-red-500/80 rounded-lg transition-colors duration-200 backdrop-blur-sm group"
                  title="Delete"
                >
                  <Trash2 className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* Statements Section */}
          <div className="p-6 space-y-6">
            {(isValue(item) ? item.statements : (item as ILeadership).statements).map((statement, statementIndex) => (
              <div
                key={statementIndex}
                className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:bg-slate-100 transition-colors duration-200"
              >
                <p className="text-slate-700 font-medium mb-4 leading-relaxed">{statement.statement}</p>

                {/* Rating Section */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Rate this statement:</span>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: 5 }).map((_, ratingIndex) => {
                      const rating = ratingIndex + 1
                      const key = `${item.id}-${statementIndex}`
                      const isSelected = selectedRatings[key] === rating

                      return (
                        <button
                          key={ratingIndex}
                          onClick={() => handleRating(item.id, statementIndex, rating)}
                          className={`w-10 h-10 rounded-lg font-semibold transition-all duration-200 hover:scale-110 ${
                            isSelected
                              ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                              : "bg-white border-2 border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-600"
                          }`}
                        >
                          {rating}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Custom Delete Confirmation Modal */}
      {deleteConfirmationOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">Confirm Deletion</h3>
                <button
                  onClick={() => setDeleteConfirmationOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-slate-600" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-slate-600 mb-6">
                Are you sure you want to delete this {isValue(itemToDelete!) ? "value" : "leadership criteria"}? This
                action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmationOpen(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    itemToDelete && isValue(itemToDelete)
                      ? onDeleteValue(itemToDelete as IValue)
                      : onDeleteLeadership(itemToDelete as ILeadership)
                  }
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <Modal
        modalId={
          selectedItem
            ? isValue(selectedItem)
              ? MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_VALUE_MODAL
              : MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_LEADERSHIP_MODAL
            : ""
        }
      >
        {selectedItem && isValue(selectedItem) ? <EditValueModal /> : <EditLeadershipModal />}
      </Modal>
    </div>
  )
}

export default CriteriaBox
